// Admins list — search + filter + sort. Rows link to the detail page;
// lock / unlock now lives there (and is also reachable via the create
// flow inline at the top).
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { fetchAdmins, fetchRoles, createAdmin, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Pagination } from "@/components/ui/Pagination";
import { SortableHeader } from "@/components/ui/SortableHeader";
import { Skeleton } from "@/components/ui/Skeleton";
import { Avatar } from "@/components/ui/Avatar";
import { useTableState } from "@/lib/useTableState";
import { fmtRelative, statusLabel } from "@/lib/format";

// Roles drives the "Initial role" dropdown — fetch a large page so
// every role is selectable without paging the form.
const ROLES_PAGE = { limit: 200, offset: 0 } as const;

interface AdminFilters {
  status: string;
  role: string;
}

export const Route = createFileRoute("/_app/admins/")({
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: keys.adminsList(undefined, 20, 0, { by: "created_at", dir: "desc" }, {}),
      queryFn: () =>
        fetchAdmins({ limit: 20, offset: 0, sort: { by: "created_at", dir: "desc" } }),
    }),
  component: AdminsList,
});

function AdminsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const table = useTableState<AdminFilters>({
    defaultSort: { by: "created_at", dir: "desc" },
    defaultFilters: { status: "", role: "" },
  });
  useEffect(() => {
    table.setPage(0);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps

  const trimmed = q.trim();
  const filtersForKey = {
    status: table.filters.status || undefined,
    role: table.filters.role || undefined,
  };
  const admins = useQuery({
    queryKey: keys.adminsList(trimmed, table.limit, table.offset, table.sort, filtersForKey),
    queryFn: () =>
      fetchAdmins({
        limit: table.limit,
        offset: table.offset,
        q: trimmed || undefined,
        sort: table.sort,
        status: table.filters.status || undefined,
        role: table.filters.role || undefined,
      }),
  });
  const roles = useQuery({
    queryKey: keys.rolesList(ROLES_PAGE.limit, ROLES_PAGE.offset),
    queryFn: () => fetchRoles(ROLES_PAGE),
  });

  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("READONLY");
  const [err, setErr] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createAdmin({ email, password, display_name: name, roles: [role] }),
    onSuccess: async () => {
      setErr(null);
      setOpen(false);
      setEmail("");
      setPassword("");
      setName("");
      await qc.invalidateQueries({ queryKey: keys.admins });
    },
    onError: (e) => setErr((e as Error).message),
    meta: { successMessage: "Admin created." },
  });

  const items = admins.data?.items ?? [];
  const openDetail = (id: string) => navigate({ to: "/admins/$id", params: { id } });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">{admins.data?.page.total ?? 0} admins</div>
        <Button onClick={() => setOpen((s) => !s)}>{open ? "Cancel" : "Create admin"}</Button>
      </div>

      {open && (
        <Card>
          <CardHeader className="border-b border-border">
            <CardTitle>New admin</CardTitle>
          </CardHeader>
          <CardBody className="space-y-3 pt-5">
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="Email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
              <Input
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                type="password"
                hint="Operator should rotate immediately."
              />
              <Input label="Display name" value={name} onChange={(e) => setName(e.target.value)} />
              <Select
                label="Initial role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                options={(roles.data?.items ?? []).map((r) => ({ value: r.name, label: r.name }))}
              />
            </div>
            {err && <p className="rounded bg-danger/10 px-3 py-2 text-xs text-danger">{err}</p>}
            <Button onClick={() => create.mutate()} loading={create.isPending} disabled={!email || !password}>
              Create
            </Button>
          </CardBody>
        </Card>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[260px] flex-1">
          <Input
            placeholder="Search by ID, name, or email…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
        <div className="w-44">
          <Select
            placeholder="Status — any"
            value={table.filters.status}
            onChange={(e) => table.setFilter("status", e.target.value)}
            // ACTIVE / LOCKED are the only listable statuses: the service
            // also has DELETED, but List() filters it out unconditionally,
            // so offering it here would just be a guaranteed-empty result.
            options={[
              { value: "ACTIVE", label: "Active" },
              { value: "LOCKED", label: "Locked" },
            ]}
          />
        </div>
        <div className="w-56">
          <Select
            placeholder="Role — any"
            value={table.filters.role}
            onChange={(e) => table.setFilter("role", e.target.value)}
            options={(roles.data?.items ?? []).map((r) => ({ value: r.name, label: r.name }))}
          />
        </div>
        {(table.hasActiveFilters || q) && (
          <Button
            variant="ghost"
            onClick={() => {
              setQ("");
              table.reset();
            }}
          >
            Reset
          </Button>
        )}
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Operators</CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {admins.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2].map((i) => <Skeleton key={i} className="h-10" />)}
            </div>
          )}
          {admins.data && items.length === 0 && (
            <p className="px-5 py-16 text-center text-sm text-muted">
              {q || table.hasActiveFilters ? "No admins match the current filters." : "No admins yet."}
            </p>
          )}
          {admins.data && items.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-2">
                  <tr>
                    <SortableHeader label="Email" field="email" sort={table.sort} onSortChange={table.setSort} className="px-5" />
                    <th className="px-3 py-3 text-left text-2xs font-medium uppercase tracking-wide text-muted">Display name</th>
                    <SortableHeader label="Status" field="status" sort={table.sort} onSortChange={table.setSort} />
                    <SortableHeader label="Created" field="created_at" sort={table.sort} onSortChange={table.setSort} align="right" />
                    <th className="px-5 py-3 text-right text-2xs font-medium uppercase tracking-wide text-muted">Last login</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((a) => (
                    <tr
                      key={a.admin_id}
                      onClick={() => openDetail(a.admin_id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          openDetail(a.admin_id);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Open admin ${a.email}`}
                      className="cursor-pointer border-b border-border/60 transition-colors last:border-0 hover:bg-neon/5 focus:bg-neon/5 focus:outline-none"
                    >
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar url={a.avatar_url} name={a.display_name || a.email} />
                          <span className="text-xs font-medium text-fg">{a.email}</span>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-xs text-muted">{a.display_name || "—"}</td>
                      <td className="px-3 py-3">
                        <Badge tone={a.status === "ACTIVE" ? "success" : a.status === "LOCKED" ? "warning" : "muted"}>
                          {statusLabel(a.status)}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-right text-2xs text-muted">{fmtRelative(a.created_at)}</td>
                      <td className="px-5 py-3 text-right text-2xs text-muted">{fmtRelative(a.last_login_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {admins.data && (
            <Pagination
              page={admins.data.page}
              currentPage={table.page}
              onPageChange={table.setPage}
              itemLabel="admins"
            />
          )}
        </CardBody>
      </Card>
    </div>
  );
}
